import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import Icon from '../AppIcon';
import Button from '../ui/Button';
import { useRequestBuilder } from '../context/RequestBuilderContext';
import RequestItemGroups from './RequestItemGroups';
import { formatRequestAmount } from './requestBuilderUtils';

const useDesktopLayout = () => {
  const [isDesktop, setIsDesktop] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(min-width: 768px)').matches
  );

  useEffect(() => {
    const media = window.matchMedia('(min-width: 768px)');
    const handleChange = (event) => setIsDesktop(event.matches);
    media.addEventListener('change', handleChange);
    return () => media.removeEventListener('change', handleChange);
  }, []);

  return isDesktop;
};

const RequestBuilderDrawer = () => {
  const {
    items,
    subtotal,
    hasRangePricing,
    isDrawerOpen,
    closeDrawer,
    clearAll,
    removeItem,
    updateQuantity,
    openForm,
  } = useRequestBuilder();
  const isDesktop = useDesktopLayout();

  useEffect(() => {
    if (!isDrawerOpen) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isDrawerOpen]);

  useEffect(() => {
    if (!isDrawerOpen) return undefined;
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') closeDrawer();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isDrawerOpen, closeDrawer]);

  return (
    <AnimatePresence>
      {isDrawerOpen && (
        <div className="fixed inset-0 z-[60]">
          <motion.button
            type="button"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeDrawer}
            className="absolute inset-0 h-full w-full bg-foreground/40"
            aria-label="Close request builder"
          />

          <motion.aside
            role="dialog"
            aria-modal="true"
            aria-labelledby="request-builder-title"
            initial={isDesktop ? { x: '100%' } : { y: '100%' }}
            animate={isDesktop ? { x: 0 } : { y: 0 }}
            exit={isDesktop ? { x: '100%' } : { y: '100%' }}
            transition={{ type: 'spring', stiffness: 320, damping: 34 }}
            className="absolute inset-x-0 bottom-0 flex max-h-[88vh] flex-col rounded-t-2xl border border-border bg-card shadow-2xl md:inset-y-0 md:left-auto md:right-0 md:max-h-none md:w-[min(460px,100vw)] md:rounded-none md:rounded-l-2xl"
          >
            <div className="mx-auto mt-2 h-1 w-10 rounded-full bg-muted-foreground/30 md:hidden" />
            <header className="flex items-center justify-between border-b border-border px-4 py-4 md:px-6">
              <div>
                <h2 id="request-builder-title" className="font-heading text-xl font-bold text-foreground">
                  Your Request
                </h2>
                <p className="mt-1 text-xs text-muted-foreground">
                  {items.length} {items.length === 1 ? 'item' : 'items'} selected
                </p>
              </div>
              <div className="flex items-center gap-1">
                <Button type="button" variant="ghost" size="sm" onClick={clearAll}>
                  Clear all
                </Button>
                <button
                  type="button"
                  onClick={closeDrawer}
                  className="rounded-md p-2 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                  aria-label="Close request builder"
                >
                  <Icon name="X" size={20} />
                </button>
              </div>
            </header>

            <div className="flex-1 overflow-y-auto px-4 py-4 custom-scrollbar md:px-6">
              {items.length > 0 ? (
                <RequestItemGroups
                  items={items}
                  editable
                  showImages
                  onRemove={removeItem}
                  onQuantityChange={updateQuantity}
                />
              ) : (
                <div className="py-16 text-center">
                  <Icon name="ClipboardList" size={42} className="mx-auto text-muted-foreground" />
                  <p className="mt-3 font-medium text-foreground">Your request is empty</p>
                </div>
              )}
            </div>

            <footer className="border-t border-border bg-muted/20 px-4 py-4 md:px-6">
              <div className="mb-3 flex items-center justify-between">
                <span className="text-sm text-muted-foreground">{hasRangePricing ? 'Starting total' : 'Estimated total'}</span>
                <span className="text-xl font-bold text-foreground">{formatRequestAmount(subtotal)}</span>
              </div>
              <Button type="button" fullWidth onClick={openForm} disabled={items.length === 0}>
                Review and Submit
              </Button>
              <p className="mt-2 text-center text-xs text-muted-foreground">
                {hasRangePricing
                  ? 'The business will confirm the final price before payment.'
                  : 'Final details and availability are confirmed by the business.'}
              </p>
            </footer>
          </motion.aside>
        </div>
      )}
    </AnimatePresence>
  );
};

export default RequestBuilderDrawer;
